class FitmentWizard {
  constructor(root) {
    this.root = root;
    this.fitmentData = this.parseJson(root.querySelector("[data-fitment-data]")) || [];
    this.fitmentDataUrl = root.dataset.fitmentUrl || "";
    this.variantMapData = this.parseJson(root.querySelector("[data-variant-map]"));
    this.state = {
      year: "",
      make: "",
      model: "",
      bodyStyle: "",
      partNumberQuery: "",
      selectedFitmentKey: "",
      material: "",
      color: "",
      variantId: "",
    };

    this.elements = {
      year: root.querySelector("[data-fitment-year]"),
      make: root.querySelector("[data-fitment-make]"),
      model: root.querySelector("[data-fitment-model]"),
      bodyStyleField: root.querySelector("[data-fitment-body-style-field]"),
      bodyStyle: root.querySelector("[data-fitment-body-style]"),
      partNumber: root.querySelector("[data-fitment-part-number]"),
      partNumberList: root.querySelector("[data-fitment-part-number-list]"),
      material: root.querySelector("[data-fitment-material]"),
      color: root.querySelector("[data-fitment-color]"),
      results: root.querySelector("[data-fitment-results]"),
      resultCount: root.querySelector("[data-fitment-result-count]"),
      empty: root.querySelector("[data-fitment-empty]"),
      notes: root.querySelector("[data-fitment-notes]"),
      reset: root.querySelector("[data-fitment-reset]"),
      add: root.querySelector("[data-fitment-add]"),
      status: root.querySelector("[data-fitment-status]"),
      productSummary: root.querySelector("[data-fitment-product]"),
      productImage: root.querySelector("[data-fitment-product-image]"),
      productTitle: root.querySelector("[data-fitment-product-title]"),
      productPrice: root.querySelector("[data-fitment-product-price]"),
      productLink: root.querySelector("[data-fitment-product-link]"),
    };

    this.cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.pendingHistoryMode = "";
    this.isApplyingUrlState = false;
    this.resultBatchSize = 24;
    this.visibleResultCount = this.resultBatchSize;
    this.lastResultSignature = "";
    this.resultsObserver = null;
    this.productLoadPromises = {};
    this.productSearchPromises = {};
    this.variantResolutionPromises = {};
    this.variantIdBySku = {};
    this.productHandleBySku = {};
    this.refreshQueued = false;
  }

  parseJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "null");
    } catch (error) {
      console.warn("Fitment wizard JSON parse error", error);
      return null;
    }
  }

  normalizeMakeName(value) {
    return String(value || "").trim().toUpperCase();
  }

  normalizeModelName(make, model) {
    const normalizedMake = this.normalizeMakeName(make);
    const normalizedModel = String(model || "").trim().toUpperCase().replace(/\s+/g, " ");

    if (normalizedMake === "SATURN") {
      if (["SATURN L SERIES", "SATURN L-SERIES", "L SERIES", "L-SERIES"].includes(normalizedModel)) {
        return "L-SERIES";
      }
    }

    return String(model || "").trim();
  }

  inferProductLine(detroitNumber, bodyStyle) {
    const body = String(bodyStyle || "").trim().toUpperCase();
    const detroit = String(detroitNumber || "").trim().toUpperCase();

    if (body) return "Carpet Kits";
    if (/^AL-/.test(detroit)) return "Leather Seat Covers";
    if (/^(L|ML)\d+/.test(detroit)) return "Vinyl";
    if (/^\d/.test(detroit)) return "Cloth";
    return "";
  }

  isStorefrontAvailableForVariantItem(item) {
    if (!item) return false;

    const published = String(item.published ?? "").trim().toLowerCase();
    const productStatus = String(item.product_status ?? "").trim().toLowerCase();

    if (published === "false") return false;
    if (productStatus === "draft" || productStatus === "archived") return false;

    return true;
  }

  isCommerceEnabledForFitment(fitment) {
    if (!fitment) return false;
    return this.getAllowedVariantItems(fitment).some((item) =>
      this.isStorefrontAvailableForVariantItem(item)
    );
  }

  async init() {
    this.variantMap = this.normalizeVariantMap();
    await this.loadFitments();
    await this.loadProductData();
    this.fitments = this.normalizeFitments();

    if (!this.fitments.length) {
      this.setStatus("No fitment data available.");
      return;
    }

    this.applyStateFromUrl(window.location.href);
    this.bindEvents();
    this.updateUI();
  }

  async loadFitments() {
    if (!this.fitmentDataUrl) return;

    try {
      const response = await fetch(this.fitmentDataUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Fitment asset request failed");
      const data = await response.json();
      if (Array.isArray(data) && data.length) {
        this.fitmentData = data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async loadProductData() {
    this.variantDetailsById = {};
    this.variantDetailPromises = {};
    const handles = Array.from(new Set((this.productHandles || []).filter(Boolean)));
    if (!handles.length) return;

    await Promise.all(handles.map((handle) => this.fetchProductByHandle(handle)));
  }

  normalizeVariantSku(value) {
    return String(value || "").trim().toUpperCase();
  }

  getVariantLookupSku(item) {
    return this.normalizeVariantSku(item?.variant_sku || item?.detroit_number);
  }

  getVariantSearchTerms(item) {
    const sku = this.getVariantLookupSku(item);
    if (!sku) return [];

    const normalized = sku.replace(/^AL-/, "");
    const parts = normalized.split("-").filter(Boolean);
    const terms = [];
    const pushTerm = (value) => {
      const next = this.normalizeVariantSku(value);
      if (!next || terms.includes(next)) return;
      terms.push(next);
    };

    if (parts[0]) pushTerm(parts[0]);
    pushTerm(normalized);
    pushTerm(sku);
    return terms;
  }

  queueRefresh() {
    if (this.refreshQueued) return;
    this.refreshQueued = true;
    window.requestAnimationFrame(() => {
      this.refreshQueued = false;
      this.updateUI();
    });
  }

  registerVariantLookup(item) {
    const sku = this.getVariantLookupSku(item);
    if (!sku) return;

    const handle = String(item?.product_handle || "").trim();
    const variantId = String(item?.variant_id || "").trim();

    if (handle) {
      this.productHandleBySku[sku] = handle;
    }

    if (variantId) {
      this.variantIdBySku[sku] = variantId;
    }
  }

  registerProductData(product) {
    const handle = String(product?.handle || "").trim();
    if (!handle) return;

    const productUrl = `/products/${handle}`;
    const productImageMap = this.buildProductImageMap(product);
    const fallbackImage =
      this.extractImageSrc(product.featured_image) ||
      this.extractImageSrc((product.images || [])[0]);

    (product.variants || []).forEach((variant) => {
      const variantId = String(variant.id || "");
      this.variantDetailsById[variantId] = {
        id: variantId,
        url: variantId ? `${productUrl}?variant=${variantId}` : productUrl,
        image: this.resolveVariantImage(variant, productImageMap, fallbackImage),
        title: variant.name || product.title || "",
        price: typeof variant.price === "number" ? variant.price / 100 : null,
      };

      const sku = this.normalizeVariantSku(variant.sku);
      if (sku) {
        this.variantIdBySku[sku] = variantId;
        this.productHandleBySku[sku] = handle;
      }
    });

    this.loadedProductHandles.add(handle);
    if (!this.productHandles.includes(handle)) {
      this.productHandles.push(handle);
    }
  }

  async fetchProductByHandle(handle) {
    const normalizedHandle = String(handle || "").trim();
    if (!normalizedHandle) return null;
    if (this.loadedProductHandles.has(normalizedHandle)) return normalizedHandle;
    if (this.productLoadPromises[normalizedHandle]) {
      return this.productLoadPromises[normalizedHandle];
    }

    this.productLoadPromises[normalizedHandle] = fetch(`/products/${normalizedHandle}.js`, {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Product lookup failed");
        return response.json();
      })
      .then((product) => {
        this.registerProductData(product);
        return normalizedHandle;
      })
      .catch(() => null)
      .finally(() => {
        delete this.productLoadPromises[normalizedHandle];
      });

    return this.productLoadPromises[normalizedHandle];
  }

  async searchProductsByTerm(term) {
    const normalizedTerm = this.normalizeVariantSku(term);
    if (!normalizedTerm) return [];
    if (this.productSearchPromises[normalizedTerm]) {
      return this.productSearchPromises[normalizedTerm];
    }

    const query = encodeURIComponent(normalizedTerm);
    const url = `/search/suggest.json?q=${query}&resources[type]=product&resources[limit]=10`;

    this.productSearchPromises[normalizedTerm] = fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Product search lookup failed");
        return response.json();
      })
      .then((payload) => {
        const products = payload?.resources?.results?.products;
        return Array.isArray(products) ? products : [];
      })
      .catch(() => [])
      .finally(() => {
        delete this.productSearchPromises[normalizedTerm];
      });

    return this.productSearchPromises[normalizedTerm];
  }

  async ensureVariantItemResolved(item) {
    if (!item) return null;

    this.registerVariantLookup(item);
    const sku = this.getVariantLookupSku(item);
    if (!sku) return null;

    const knownHandle = String(item.product_handle || this.productHandleBySku[sku] || "").trim();
    if (knownHandle && !this.loadedProductHandles.has(knownHandle)) {
      await this.fetchProductByHandle(knownHandle);
    }

    const knownVariantId = String(item.variant_id || this.variantIdBySku[sku] || "").trim();
    if (knownVariantId) {
      item.variant_id = knownVariantId;
      if (!item.product_handle && this.productHandleBySku[sku]) {
        item.product_handle = this.productHandleBySku[sku];
      }
      const detail = this.variantDetailsById[knownVariantId] || await this.ensureVariantDetail(knownVariantId);
      if (detail) return detail;
    }

    if (this.variantResolutionPromises[sku]) {
      return this.variantResolutionPromises[sku];
    }

    this.variantResolutionPromises[sku] = (async () => {
      for (const term of this.getVariantSearchTerms(item)) {
        const products = await this.searchProductsByTerm(term);
        for (const product of products) {
          const handle = String(product?.handle || "").trim();
          if (!handle) continue;
          await this.fetchProductByHandle(handle);

          const resolvedVariantId = String(this.variantIdBySku[sku] || "").trim();
          if (!resolvedVariantId) continue;

          item.variant_id = resolvedVariantId;
          item.product_handle = String(this.productHandleBySku[sku] || handle).trim();
          return this.variantDetailsById[resolvedVariantId] || this.ensureVariantDetail(resolvedVariantId);
        }
      }

      return null;
    })().finally(() => {
      delete this.variantResolutionPromises[sku];
    });

    return this.variantResolutionPromises[sku];
  }

  getVariantProductHandle(item) {
    const rowHandle = String(item?.product_handle || "").trim();
    if (rowHandle) return rowHandle;
    return this.productHandle || "";
  }

  getFitmentProductHandles(fitment) {
    return Array.from(
      new Set(
        this.variantMap
          .filter((item) => item.detroit_number === fitment.detroitNumber)
          .map((item) => this.getVariantProductHandle(item))
          .filter(Boolean)
      )
    );
  }

  getProductHandlesFromVariantMap() {
    return Array.from(
      new Set(
        this.variantMap
          .map((item) => this.getVariantProductHandle(item))
          .filter(Boolean)
      )
    );
  }

  async ensureProductDataForFitment(fitment) {
    const missingHandles = this.getFitmentProductHandles(fitment).filter(
      (handle) => !this.loadedProductHandles.has(handle)
    );
    if (!missingHandles.length) return;

    await Promise.all(missingHandles.map((handle) => this.fetchProductByHandle(handle)));
  }

  async ensureVariantDetail(variantId) {
    const id = String(variantId || "");
    if (!id) return null;

    const existing = this.variantDetailsById[id];
    if (existing && existing.image) return existing;

    if (this.variantDetailPromises[id]) {
      return this.variantDetailPromises[id];
    }

    this.variantDetailPromises[id] = fetch(`/variants/${id}.js`, {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Variant detail lookup failed");
        return response.json();
      })
      .then((variant) => {
        const current = this.variantDetailsById[id] || { id };
        const next = {
          ...current,
          id,
          url: variant.url || current.url || "",
          image:
            this.extractImageSrc(variant.featured_image) ||
            this.extractImageSrc(variant.image) ||
            current.image ||
            "",
          title: variant.name || current.title || "",
          price: typeof variant.price === "number" ? variant.price / 100 : current.price ?? null,
        };
        this.variantDetailsById[id] = next;
        return next;
      })
      .catch((error) => {
        console.error(error);
        return this.variantDetailsById[id] || null;
      })
      .finally(() => {
        delete this.variantDetailPromises[id];
      });

    return this.variantDetailPromises[id];
  }

  buildProductImageMap(product) {
    const imageMap = {};
    const assignImage = (key, value) => {
      if (!key || !value) return;
      imageMap[String(key)] = value;
    };

    (product.images || []).forEach((image, index) => {
      if (typeof image === "string") {
        assignImage(index, image);
        return;
      }
      assignImage(image.id, this.extractImageSrc(image));
      assignImage(image.position, this.extractImageSrc(image));
      assignImage(index, this.extractImageSrc(image));
    });

    (product.media || []).forEach((media, index) => {
      const image = this.extractImageSrc(media.preview_image || media);
      assignImage(media.id, image);
      assignImage(media.position, image);
      assignImage(index, image);
    });

    return imageMap;
  }

  extractImageSrc(image) {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.src || image.url || image.preview_image?.src || image.preview_image?.url || "";
  }

  resolveVariantImage(variant, imageMap, fallbackImage) {
    return (
      this.extractImageSrc(variant.featured_image) ||
      this.extractImageSrc(variant.image) ||
      imageMap[String(variant.featured_media?.id || "")] ||
      imageMap[String(variant.image_id || "")] ||
      imageMap[String(variant.featured_image?.id || "")] ||
      imageMap[String(variant.position || "")] ||
      fallbackImage ||
      ""
    );
  }

  normalizeFitments() {
    const rawList = Array.isArray(this.fitmentData) ? this.fitmentData : [];

    return rawList
      .map((raw) => {
        try {
          const getField = (key) => {
            if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key];
            const camelKey = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            if (raw && Object.prototype.hasOwnProperty.call(raw, camelKey)) return raw[camelKey];
            const fields = raw && raw.fields;
            if (Array.isArray(fields)) {
              const match = fields.find((field) => field && (field.key === key || field.handle === key));
              if (match) {
                return (
                  match.value ??
                  match.value_text ??
                  match.value_number ??
                  match.value_boolean ??
                  match
                );
              }
            } else if (fields && typeof fields === "object") {
              const field = fields[key];
              if (field && typeof field === "object" && "value" in field) return field.value;
              return field;
            }
            return null;
          };

          const parseList = (value) => {
            if (Array.isArray(value)) {
              return value
                .map((item) => String(item || "").trim())
                .filter(Boolean);
            }

            return String(value || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);
          };

          const detroitNumber = String(getField("detroit_number") || "").trim();
          const makes = parseList(getField("make"));
          const models = parseList(getField("model")).map((model) =>
            this.normalizeModelName(makes[0] || "", model)
          );
          const yearStart = Number(getField("year_start")) || null;
          const yearEnd = Number(getField("year_end")) || null;
          const bodyStyle = String(getField("body_style") || "").trim();

          return {
            fitmentKey: [
              detroitNumber,
              yearStart || "",
              yearEnd || "",
              makes.join("|"),
              models.join("|"),
              bodyStyle,
            ].join("::"),
            detroitNumber,
            make: makes,
            model: models,
            bodyStyle,
            productLine: this.inferProductLine(detroitNumber, bodyStyle),
            yearStart,
            yearEnd,
            allowedMaterials: parseList(getField("allowed_materials")),
            notes: String(getField("notes") || "").trim(),
            active: String(getField("active") || "").toLowerCase() !== "false",
          };
        } catch (error) {
          console.warn("Skipping invalid fitment row", error, raw);
          return null;
        }
      })
      .filter((fitment) => fitment && fitment.detroitNumber && fitment.active);
  }

  normalizeVariantMap() {
    if (!this.variantMapData) return [];
    if (Array.isArray(this.variantMapData)) {
      this.productHandle = "";
      this.variantMapData.forEach((item) => this.registerVariantLookup(item));
      this.productHandles = Array.from(
        new Set(
          this.variantMapData
            .map((item) => String(item.product_handle || "").trim())
            .filter(Boolean)
        )
      );
      this.loadedProductHandles = new Set();
      return this.variantMapData;
    }
    if (Array.isArray(this.variantMapData.variants)) {
      this.productHandle = String(this.variantMapData.product_handle || "").trim();
      this.variantMapData.variants.forEach((item) => this.registerVariantLookup(item));
      this.productHandles = Array.from(
        new Set(
          this.variantMapData.variants
            .map((item) => String(item.product_handle || "").trim())
            .concat(this.productHandle ? [this.productHandle] : [])
            .filter(Boolean)
        )
      );
      this.loadedProductHandles = new Set();
      return this.variantMapData.variants;
    }
    this.productHandle = "";
    this.productHandles = [];
    this.loadedProductHandles = new Set();
    return [];
  }

  bindEvents() {
    this.elements.year.addEventListener("change", () => {
      this.state.year = this.elements.year.value;
      this.requestUrlSync("push");
      this.updateUI();
    });

    this.elements.make.addEventListener("change", () => {
      this.state.make = this.elements.make.value;
      this.requestUrlSync("push");
      this.updateUI();
    });

    this.elements.model.addEventListener("change", () => {
      this.state.model = this.elements.model.value;
      this.requestUrlSync("push");
      this.updateUI();
    });

    if (this.elements.bodyStyle) {
      this.elements.bodyStyle.addEventListener("change", () => {
        this.state.bodyStyle = this.elements.bodyStyle.value;
        this.requestUrlSync("push");
        this.updateUI();
      });
    }

    this.elements.partNumber.addEventListener("input", () => {
      this.state.partNumberQuery = this.elements.partNumber.value.trim().toUpperCase();
      this.requestUrlSync("replace");
      this.updateUI();
    });

    this.elements.material.addEventListener("change", () => {
      this.state.material = this.elements.material.value;
      this.requestUrlSync("push");
      this.updateUI();
    });

    this.elements.color.addEventListener("change", () => {
      this.state.color = this.elements.color.value;
      this.requestUrlSync("push");
      this.updateUI();
    });

    this.elements.reset.addEventListener("click", () => this.resetAll());
    this.elements.add.addEventListener("click", () => this.addToCart());

    window.addEventListener("popstate", () => {
      this.applyStateFromUrl(window.location.href);
      this.updateUI();
    });
  }

  resetAll() {
    this.state = {
      year: "",
      make: "",
      model: "",
      bodyStyle: "",
      partNumberQuery: "",
      selectedFitmentKey: "",
      material: "",
      color: "",
      variantId: "",
    };
    this.elements.partNumber.value = "";
    this.requestUrlSync("push");
    this.updateUI();
  }

  requestUrlSync(mode = "push") {
    if (this.isApplyingUrlState) return;
    if (this.pendingHistoryMode === "push") return;
    this.pendingHistoryMode = mode;
  }

  resolveOptionValue(value, options) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const lower = raw.toLowerCase();
    return options.find((option) => String(option).toLowerCase() === lower) || "";
  }

  applyStateFromUrl(url) {
    this.isApplyingUrlState = true;

    try {
      const nextUrl = new URL(url, window.location.origin);
      const params = nextUrl.searchParams;
      const allMakes = Array.from(new Set(this.fitments.flatMap((fitment) => fitment.make)));
      const allModels = Array.from(new Set(this.fitments.flatMap((fitment) => fitment.model)));
      const allMaterials = Array.from(new Set(this.fitments.map((fitment) => fitment.productLine).filter(Boolean)));
      const allBodyStyles = Array.from(new Set(this.fitments.map((fitment) => fitment.bodyStyle).filter(Boolean)));
      const allColors = Array.from(new Set(this.variantMap.map((item) => item.color).filter(Boolean)));

      this.state.year = params.get("year") || "";
      this.state.make = this.resolveOptionValue(params.get("make"), allMakes);
      this.state.model = this.resolveOptionValue(params.get("model"), allModels);
      this.state.material = this.resolveOptionValue(params.get("material"), allMaterials);
      this.state.bodyStyle = this.resolveOptionValue(params.get("body_style"), allBodyStyles);
      this.state.partNumberQuery = String(params.get("part_number") || "").trim().toUpperCase();
      this.state.color = this.resolveOptionValue(params.get("color"), allColors);
      this.state.selectedFitmentKey = "";
      this.state.variantId = "";
    } finally {
      this.isApplyingUrlState = false;
    }
  }

  syncUrlState() {
    if (this.isApplyingUrlState || !this.pendingHistoryMode) return;

    const url = new URL(window.location.href);
    const managedParams = ["year", "make", "model", "material", "body_style", "part_number", "color"];
    managedParams.forEach((key) => url.searchParams.delete(key));

    if (this.state.year) url.searchParams.set("year", String(this.state.year));
    if (this.state.make) url.searchParams.set("make", this.state.make.toLowerCase());
    if (this.state.model) url.searchParams.set("model", this.state.model.toLowerCase());
    if (this.state.material) url.searchParams.set("material", this.state.material.toLowerCase());
    if (this.state.bodyStyle) url.searchParams.set("body_style", this.state.bodyStyle.toLowerCase());
    if (this.state.partNumberQuery) url.searchParams.set("part_number", this.state.partNumberQuery.toLowerCase());
    if (this.state.color) url.searchParams.set("color", this.state.color.toLowerCase());

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl !== currentUrl) {
      if (this.pendingHistoryMode === "replace") {
        window.history.replaceState({}, "", nextUrl);
      } else {
        window.history.pushState({}, "", nextUrl);
      }
    }

    this.pendingHistoryMode = "";
  }

  updateUI() {
    let changed = true;

    while (changed) {
      changed = false;

      const availableYears = this.getAvailableYears();
      const availableMakes = this.getAvailableMakes();
      const availableModels = this.getAvailableModels();
      const availableBodyStyles = this.getAvailableBodyStyles();
      const availableMaterials = this.getAvailableMaterials();
      const availableColors = this.getAvailableColors();
      const filteredFitments = this.getFilteredFitments();

      if (this.state.year && !availableYears.includes(Number(this.state.year))) {
        this.state.year = "";
        changed = true;
      }

      if (this.state.make && !availableMakes.includes(this.state.make)) {
        this.state.make = "";
        changed = true;
      }

      if (this.state.model && !availableModels.includes(this.state.model)) {
        this.state.model = "";
        changed = true;
      }

      if (this.state.bodyStyle && !availableBodyStyles.includes(this.state.bodyStyle)) {
        this.state.bodyStyle = "";
        changed = true;
      }

      if (this.state.material && !availableMaterials.includes(this.state.material)) {
        this.state.material = "";
        changed = true;
      }

      if (this.state.color && !availableColors.includes(this.state.color)) {
        this.state.color = "";
        changed = true;
      }

      if (
        this.state.selectedFitmentKey &&
        !filteredFitments.some((fitment) => fitment.fitmentKey === this.state.selectedFitmentKey)
      ) {
        this.state.selectedFitmentKey = "";
        changed = true;
      }
    }

    const filteredFitments = this.getFilteredFitments();
    const displayFitments = this.getDisplayFitments(filteredFitments);

    if (!this.state.selectedFitmentKey && displayFitments.length === 1) {
      this.state.selectedFitmentKey = displayFitments[0].fitmentKey;
    }

    const selectedFitment = this.getSelectedFitment(filteredFitments);
    this.state.variantId = this.findVariantId(selectedFitment);

    this.populateOptions(this.elements.year, this.getAvailableYears(), "All years");
    this.populateOptions(this.elements.make, this.getAvailableMakes(), "All makes");
    this.populateOptions(this.elements.model, this.getAvailableModels(), "All models");
    if (this.elements.bodyStyle) {
      this.populateOptions(this.elements.bodyStyle, this.getAvailableBodyStyles(), "All body styles");
    }
    this.populateOptions(this.elements.material, this.getAvailableMaterials(), "All materials");
    this.populateOptions(this.elements.color, this.getAvailableColors(), "All colors");
    this.populatePartNumberSuggestions();

    this.syncFieldValues();
    this.updateBodyStyleVisibility();
    this.syncUrlState();
    this.renderResults(filteredFitments);
    this.renderNotes(selectedFitment);
    this.updateProductSummary();
    this.updateAddToCart();
  }

  syncFieldValues() {
    this.elements.year.value = this.state.year;
    this.elements.make.value = this.state.make;
    this.elements.model.value = this.state.model;
    if (this.elements.bodyStyle) this.elements.bodyStyle.value = this.state.bodyStyle;
    this.elements.partNumber.value = this.state.partNumberQuery;
    this.elements.material.value = this.state.material;
    this.elements.color.value = this.state.color;
  }

  updateBodyStyleVisibility() {
    if (!this.elements.bodyStyleField || !this.elements.bodyStyle) return;
    const showBodyStyle = this.state.material === "Carpet Kits";
    this.elements.bodyStyleField.hidden = !showBodyStyle;
    this.elements.bodyStyle.disabled = !showBodyStyle;
    if (!showBodyStyle && this.state.bodyStyle) {
      this.state.bodyStyle = "";
    }
  }

  populateOptions(select, options, placeholder) {
    if (!select) return;

    const currentValue = select.value;
    select.innerHTML = "";

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    options.forEach((optionValue) => {
      const option = document.createElement("option");
      option.value = String(optionValue);
      option.textContent = String(optionValue);
      select.appendChild(option);
    });

    select.disabled = options.length === 0;

    if (currentValue && options.map(String).includes(currentValue)) {
      select.value = currentValue;
    }
  }

  populatePartNumberSuggestions() {
    if (!this.elements.partNumberList) return;

    const partNumbers = Array.from(
      new Set(
        this.getFilteredFitments("partNumber").map((fitment) => fitment.detroitNumber)
      )
    ).sort();

    this.elements.partNumberList.innerHTML = "";
    partNumbers.forEach((partNumber) => {
      const option = document.createElement("option");
      option.value = partNumber;
      this.elements.partNumberList.appendChild(option);
    });
  }

  getAvailableYears() {
    const years = new Set();
    this.getFilteredFitments("year").forEach((fitment) => {
      if (!fitment.yearStart || !fitment.yearEnd) return;
      for (let year = fitment.yearStart; year <= fitment.yearEnd; year += 1) {
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }

  getAvailableMakes() {
    const makes = new Set();
    this.getFilteredFitments("make").forEach((fitment) => {
      fitment.make.forEach((make) => makes.add(make));
    });
    return Array.from(makes).sort();
  }

  getAvailableModels() {
    const models = new Set();
    this.getFilteredFitments("model").forEach((fitment) => {
      fitment.model.forEach((model) => models.add(model));
    });
    return Array.from(models).sort();
  }

  getAvailableBodyStyles() {
    const bodyStyles = new Set();
    this.getFilteredFitments("bodyStyle").forEach((fitment) => {
      if (fitment.bodyStyle) bodyStyles.add(fitment.bodyStyle);
    });
    return Array.from(bodyStyles).sort();
  }

  getAvailableMaterials() {
    const materials = new Set();
    this.getFilteredFitments("material").forEach((fitment) => {
      if (fitment.productLine) materials.add(fitment.productLine);
    });
    const order = [
      "Vinyl",
      "Cloth",
      "Carpet Kits",
      "Floor Mats",
      "Leather Seat Covers",
      "Trunkliner",
      "Headliner",
    ];
    return Array.from(materials).sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }

  getAvailableColors() {
    const colors = new Set();
    this.getFilteredFitments("color").forEach((fitment) => {
      this.getAllowedVariantItems(fitment).forEach((item) => {
        if (!this.state.material || item.material === this.state.material) {
          colors.add(item.color);
        }
      });
    });
    return Array.from(colors).sort();
  }

  getAllowedVariantItems(fitment) {
    const allowed = fitment.allowedMaterials || [];
    return this.variantMap.filter((item) => {
      if (item.detroit_number !== fitment.detroitNumber) return false;
      if (allowed.length && !allowed.includes(item.material)) return false;
      return true;
    });
  }

  getFilteredFitments(excludeField = "") {
    return this.fitments
      .filter((fitment) => {
        if (excludeField !== "year" && this.state.year) {
          if (!this.yearMatches(fitment, Number(this.state.year))) return false;
        }

        if (excludeField !== "make" && this.state.make) {
          if (!this.matchesSelection(fitment.make, this.state.make)) return false;
        }

        if (excludeField !== "model" && this.state.model) {
          if (!this.matchesSelection(fitment.model, this.state.model)) return false;
        }

        if (excludeField !== "material" && this.state.material) {
          if (fitment.productLine !== this.state.material) return false;
        }

        if (excludeField !== "bodyStyle" && this.state.bodyStyle) {
          if (fitment.bodyStyle !== this.state.bodyStyle) return false;
        }

        if (excludeField !== "partNumber" && this.state.partNumberQuery) {
          if (!fitment.detroitNumber.toLowerCase().includes(this.state.partNumberQuery.toLowerCase())) {
            return false;
          }
        }

        const materialFilter = "";
        const colorFilter = excludeField === "color" ? "" : this.state.color;
        if (!this.fitmentMatchesVariantFilters(fitment, materialFilter, colorFilter)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.detroitNumber !== b.detroitNumber) {
          return a.detroitNumber.localeCompare(b.detroitNumber);
        }
        if ((a.yearStart || 0) !== (b.yearStart || 0)) {
          return (a.yearStart || 0) - (b.yearStart || 0);
        }
        return a.fitmentKey.localeCompare(b.fitmentKey);
      });
  }

  fitmentMatchesVariantFilters(fitment, material, color) {
    const variants = this.getAllowedVariantItems(fitment);
    if (!variants.length) return false;

    return variants.some((item) => {
      if (material && item.material !== material) return false;
      if (color && item.color !== color) return false;
      return true;
    });
  }

  getSelectedFitment(filteredFitments) {
    return filteredFitments.find((fitment) => fitment.fitmentKey === this.state.selectedFitmentKey) || null;
  }

  getDisplayFitments(filteredFitments) {
    if (this.state.model) return filteredFitments;

    const grouped = new Map();

    filteredFitments.forEach((fitment) => {
      const key = `${fitment.productLine || ""}::${fitment.detroitNumber}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          ...fitment,
          make: [...fitment.make],
          model: [...fitment.model],
          yearStart: fitment.yearStart,
          yearEnd: fitment.yearEnd,
        });
        return;
      }

      const existing = grouped.get(key);
      existing.make = Array.from(new Set([...existing.make, ...fitment.make])).sort();
      existing.model = Array.from(new Set([...existing.model, ...fitment.model])).sort();
      existing.yearStart = Math.min(existing.yearStart || fitment.yearStart || 0, fitment.yearStart || existing.yearStart || 0) || null;
      existing.yearEnd = Math.max(existing.yearEnd || fitment.yearEnd || 0, fitment.yearEnd || existing.yearEnd || 0) || null;
    });

    return Array.from(grouped.values()).sort((a, b) => {
      if (a.detroitNumber !== b.detroitNumber) {
        return a.detroitNumber.localeCompare(b.detroitNumber);
      }
      return (a.fitmentKey || "").localeCompare(b.fitmentKey || "");
    });
  }

  renderResults(filteredFitments) {
    if (!this.elements.results) return;

    const displayFitments = this.getDisplayFitments(filteredFitments);
    const resultSignature = displayFitments.map((fitment) => fitment.fitmentKey).join("|");
    if (resultSignature !== this.lastResultSignature) {
      this.lastResultSignature = resultSignature;
      this.visibleResultCount = this.resultBatchSize;
    }
    const visibleFitments = displayFitments.slice(0, this.visibleResultCount);

    this.teardownResultsObserver();
    this.elements.results.innerHTML = "";
    this.elements.resultCount.textContent = displayFitments.length
      ? `${displayFitments.length} matching part${displayFitments.length === 1 ? "" : "s"}`
      : "";

    if (!displayFitments.length) {
      this.elements.empty.hidden = false;
      this.elements.empty.textContent = "No matches found for that combination.";
      return;
    }

    this.elements.empty.hidden = true;

    visibleFitments.forEach((fitment, index) => {
      const card = document.createElement("article");
      card.className = "fitment-wizard__result";
      card.style.setProperty("--result-index", index);
      card.tabIndex = 0;
      if (fitment.fitmentKey === this.state.selectedFitmentKey) {
        card.classList.add("is-active");
      }

      const grains = Array.from(
        new Set(this.getAllowedVariantItems(fitment).map((item) => item.material))
      );
      const preview = this.getResultPreview(fitment);
      const variantItem = this.getAllowedVariantItems(fitment).find(
        (item) => String(item.variant_id || "") === String(preview.variantId || "")
      ) || this.getAllowedVariantItems(fitment)[0] || null;
      const commerceEnabled = this.isCommerceEnabledForFitment(fitment);
      const canAddCardResult = commerceEnabled && Boolean(preview.variantId);
      const canViewProduct = Boolean(preview.url);
      const cardLinkMarkup = canViewProduct
        ? `
        <button
          type="button"
          class="fitment-wizard__result-link button button--secondary"
          data-result-link
        >
          View product
        </button>
      `
        : "";
      const cardActionMarkup = `
        <button
          type="button"
          class="fitment-wizard__result-add button${canAddCardResult ? "" : " button--secondary"}"
          data-result-add
          ${canAddCardResult ? "" : "disabled"}
        >
          ${canAddCardResult ? "Add to cart" : commerceEnabled ? "Unavailable" : "Coming soon"}
        </button>
      `;

      card.innerHTML = `
        <div class="fitment-wizard__result-top">
          ${
            canViewProduct
              ? `
          <button
            type="button"
            class="fitment-wizard__result-thumb fitment-wizard__result-view${preview.image ? "" : " is-empty"}"
            data-result-view
          >
            ${preview.image ? `<img src="${this.escapeHtml(preview.image)}" alt="${this.escapeHtml(preview.title || fitment.detroitNumber)}" loading="lazy">` : ""}
          </button>
          `
              : `
          <div class="fitment-wizard__result-thumb${preview.image ? "" : " is-empty"}">
            ${preview.image ? `<img src="${this.escapeHtml(preview.image)}" alt="${this.escapeHtml(preview.title || fitment.detroitNumber)}" loading="lazy">` : ""}
          </div>
          `
          }
          <div class="fitment-wizard__result-main">
            ${preview.title ? `
              ${canViewProduct ? `
              <button
                type="button"
                class="fitment-wizard__result-title fitment-wizard__result-view"
                data-result-view
              >
                ${this.escapeHtml(preview.title)}
              </button>
              ` : `
              <div class="fitment-wizard__result-title">
                ${this.escapeHtml(preview.title)}
              </div>
              `}
            ` : ""}
            <span class="fitment-wizard__result-years">${this.escapeHtml(this.formatYearRange(fitment))}</span>
            <div class="fitment-wizard__result-vehicle">
              ${this.escapeHtml(fitment.make.join(", "))} · ${this.escapeHtml(fitment.model.join(", "))}
            </div>
            ${preview.price !== null ? `<div class="fitment-wizard__result-price">${this.escapeHtml(this.formatPrice(preview.price))}</div>` : ""}
          </div>
        </div>
        <div class="fitment-wizard__result-footer">
          <div class="fitment-wizard__result-pills">
            ${grains.map((grain) => `<span class="fitment-wizard__pill">${this.escapeHtml(grain)}</span>`).join("")}
          </div>
          ${
            canViewProduct
              ? `
          <button
            type="button"
            class="fitment-wizard__result-part fitment-wizard__result-view"
            data-result-view
          >
            ${this.escapeHtml(fitment.detroitNumber)}
          </button>
          `
              : `
          <div class="fitment-wizard__result-part">
            ${this.escapeHtml(fitment.detroitNumber)}
          </div>
          `
          }
          ${cardLinkMarkup}
          ${cardActionMarkup}
        </div>
      `;

      const selectCard = () => {
        this.state.selectedFitmentKey = fitment.fitmentKey;
        this.state.partNumberQuery = fitment.detroitNumber;
        this.updateUI();
      };

      card.addEventListener("click", (event) => {
        if (event.target.closest("[data-result-add]")) return;
        if (canViewProduct) {
          this.navigateToProductUrl(preview.url, event);
          return;
        }
        selectCard();
      });
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (canViewProduct) {
            this.navigateToProductUrl(preview.url, event);
            return;
          }
          selectCard();
        }
      });

      const addButton = card.querySelector("[data-result-add]");
      if (addButton) {
        addButton.addEventListener("click", (event) => {
          event.stopPropagation();
          if (!preview.variantId) return;
          this.state.selectedFitmentKey = fitment.fitmentKey;
          this.state.partNumberQuery = fitment.detroitNumber;
          this.state.variantId = preview.variantId;
          this.addToCart(preview.variantId, fitment);
        });
      }

      const viewTargets = card.querySelectorAll("[data-result-link], [data-result-view]");
      viewTargets.forEach((node) => {
        node.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateToProductUrl(preview.url, event);
        });
      });

      if (variantItem && (!preview.image || !preview.url || !preview.variantId)) {
        this.ensureVariantItemResolved(variantItem).then((details) => {
          if (!card.isConnected || !details) return;
          this.queueRefresh();
        });
      } else if (!preview.image && preview.variantId) {
        const thumb = card.querySelector(".fitment-wizard__result-thumb");
        const titleNode = card.querySelector(".fitment-wizard__result-title");
        const priceNode = card.querySelector(".fitment-wizard__result-price");
        this.ensureVariantDetail(preview.variantId).then((details) => {
          if (!card.isConnected || !details) return;
          if (thumb && details.image && thumb.isConnected) {
            thumb.classList.remove("is-empty");
            thumb.innerHTML = `<img src="${this.escapeHtml(details.image)}" alt="${this.escapeHtml(details.title || fitment.detroitNumber)}" loading="lazy">`;
          }
          if (titleNode && details.title) {
            titleNode.textContent = details.title;
          }
          if (priceNode && typeof details.price === "number") {
            priceNode.textContent = this.formatPrice(details.price);
          }
        });
      }

      this.elements.results.appendChild(card);
    });

    if (visibleFitments.length < displayFitments.length) {
      const sentinel = document.createElement("div");
      sentinel.className = "fitment-wizard__results-sentinel";
      sentinel.setAttribute("aria-hidden", "true");
      this.elements.results.appendChild(sentinel);
      this.setupResultsObserver(sentinel, filteredFitments, displayFitments.length);
    }
  }

  setupResultsObserver(sentinel, filteredFitments, totalCount) {
    if (!sentinel || !("IntersectionObserver" in window)) return;

    this.resultsObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.visibleResultCount = Math.min(
        this.visibleResultCount + this.resultBatchSize,
        totalCount
      );
      this.teardownResultsObserver();
      this.renderResults(filteredFitments);
    }, {
      rootMargin: "800px 0px",
    });

    this.resultsObserver.observe(sentinel);
  }

  teardownResultsObserver() {
    if (!this.resultsObserver) return;
    this.resultsObserver.disconnect();
    this.resultsObserver = null;
  }

  navigateToProductUrl(url, event) {
    const href = String(url || "").trim();
    if (!href || href === "#") return;

    if (event && (event.metaKey || event.ctrlKey)) {
      window.open(href, "_blank", "noopener");
      return;
    }

    if (window.Shopify && window.Shopify.designMode) {
      window.open(href, "_blank", "noopener");
      return;
    }

    window.location.href = href;
  }

  renderNotes(selectedFitment) {
    if (!this.elements.notes) return;
    this.elements.notes.textContent = selectedFitment && selectedFitment.notes ? selectedFitment.notes : "";
  }

  findVariantId(selectedFitment) {
    return this.findBestVariantIdForFitment(selectedFitment);
  }

  findVariantIdForFitment(fitment, material, color) {
    if (!fitment || !material || !color) return "";

    const match = this.variantMap.find(
      (item) =>
        item.detroit_number === fitment.detroitNumber &&
        item.material === material &&
        item.color === color
    );

    return match ? String(match.variant_id || "") : "";
  }

  findBestVariantIdForFitment(fitment) {
    if (!fitment) return "";
    if (!this.isCommerceEnabledForFitment(fitment)) return "";

    const exactVariantId = this.findVariantIdForFitment(
      fitment,
      "",
      this.state.color
    );
    if (exactVariantId) return exactVariantId;

    const firstVariant = this.getAllowedVariantItems(fitment)[0];
    return firstVariant ? String(firstVariant.variant_id || "") : "";
  }

  getResultPreview(fitment) {
    if (!fitment) {
      return { variantId: "", exactVariantId: "", image: "", title: "", price: null, url: "" };
    }

    const exactVariantId = this.findVariantIdForFitment(
      fitment,
      "",
      this.state.color
    );
    const variantId = this.findBestVariantIdForFitment(fitment);

    const details = this.variantDetailsById && this.variantDetailsById[variantId]
      ? this.variantDetailsById[variantId]
      : null;
    const variantItem = this.getAllowedVariantItems(fitment).find(
      (item) => String(item.variant_id || "") === variantId
    ) || this.getAllowedVariantItems(fitment)[0] || null;
    const handle = variantItem ? this.getVariantProductHandle(variantItem) : "";
    const fallbackUrl = variantId && handle ? `/products/${handle}?variant=${variantId}` : handle ? `/products/${handle}` : "";
    const fallbackTitle = String(variantItem?.product_title || "").trim();
    const fallbackPrice = Number.parseFloat(String(variantItem?.variant_price || ""));

    return {
      variantId,
      exactVariantId,
      image: details ? details.image : "",
      title: details && details.title ? details.title : fallbackTitle,
      price: details && typeof details.price === "number"
        ? details.price
        : Number.isFinite(fallbackPrice)
          ? fallbackPrice
          : null,
      url: details && details.url ? details.url : fallbackUrl,
    };
  }

  formatPrice(price) {
    if (typeof price !== "number" || Number.isNaN(price)) return "";
    return `$${price.toFixed(2)}`;
  }

  updateAddToCart() {
    const selectedFitment = this.getSelectedFitment(this.getFilteredFitments());
    const enabled = Boolean(this.state.variantId) && this.isCommerceEnabledForFitment(selectedFitment);
    this.elements.add.disabled = !enabled;
  }

  resetProductSummary() {
    if (!this.elements.productSummary) return;
    this.elements.productSummary.hidden = true;
    if (this.elements.productImage) this.elements.productImage.src = "";
    if (this.elements.productTitle) this.elements.productTitle.textContent = "";
    if (this.elements.productPrice) this.elements.productPrice.textContent = "";
    if (this.elements.productLink) this.elements.productLink.href = "#";
  }

  async updateProductSummary() {
    if (!this.elements.productSummary) return;

    const selectedFitment = this.getSelectedFitment(this.getFilteredFitments());
    if (!this.isCommerceEnabledForFitment(selectedFitment)) {
      this.resetProductSummary();
      return;
    }

    if (!this.state.variantId) {
      this.resetProductSummary();
      return;
    }

    try {
      const response = await fetch(`/variants/${this.state.variantId}.js`);
      if (!response.ok) throw new Error("Variant lookup failed");
      const data = await response.json();
      const price = typeof data.price === "number" ? data.price / 100 : null;

      if (this.elements.productImage) {
        if (data.featured_image && data.featured_image.src) {
          this.elements.productImage.src = data.featured_image.src;
          this.elements.productImage.alt = data.name || "";
        } else {
          this.elements.productImage.src = "";
          this.elements.productImage.alt = "";
        }
      }

      if (this.elements.productTitle) {
        this.elements.productTitle.textContent = data.name || "Selected product";
      }

      if (this.elements.productPrice) {
        this.elements.productPrice.textContent = price !== null ? `$${price.toFixed(2)}` : "";
      }

      if (this.elements.productLink) {
        this.elements.productLink.href = data.url || "#";
      }

      this.elements.productSummary.hidden = false;
    } catch (error) {
      console.error(error);
      this.resetProductSummary();
    }
  }

  async addToCart(variantId = this.state.variantId, selectedFitment = this.getSelectedFitment(this.getFilteredFitments())) {
    if (!variantId || !selectedFitment) return;
    if (!this.isCommerceEnabledForFitment(selectedFitment)) {
      this.setStatus("Cloth products are not available for direct purchase yet.");
      return;
    }

    this.setStatus("Adding to cart...");

    try {
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      formData.append("properties[Detroit Number]", selectedFitment.detroitNumber);
      formData.append(
        "properties[Vehicle]",
        `${this.state.year} ${selectedFitment.make[0] || ""} ${selectedFitment.model[0] || ""}`.trim()
      );

      if (this.cart) {
        formData.append(
          "sections",
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
        this.cart.setActiveElement(this.elements.add);
      }

      config.body = formData;
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || "Add to cart failed");
      }

      if (this.cart && typeof this.cart.renderContents === "function") {
        this.cart.renderContents(data);
      }

      if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "fitment-wizard",
          productVariantId: variantId,
          cartData: data,
        });
      }

      this.setStatus("Added to cart.");
    } catch (error) {
      console.error(error);
      this.setStatus("Unable to add to cart. Check variant mapping.");
    }
  }

  hasActiveFilters() {
    return Boolean(
      this.state.year ||
      this.state.make ||
      this.state.model ||
      this.state.bodyStyle ||
      this.state.partNumberQuery ||
      this.state.material ||
      this.state.color
    );
  }

  formatYearRange(fitment) {
    if (!fitment.yearStart && !fitment.yearEnd) return "Year range unavailable";
    if (fitment.yearStart && fitment.yearEnd && fitment.yearStart !== fitment.yearEnd) {
      return `${fitment.yearStart} to ${fitment.yearEnd}`;
    }
    return String(fitment.yearStart || fitment.yearEnd || "");
  }

  yearMatches(fitment, year) {
    if (!year || !fitment.yearStart || !fitment.yearEnd) return false;
    return year >= fitment.yearStart && year <= fitment.yearEnd;
  }

  matchesSelection(list, selection) {
    if (!selection) return true;
    return list.some((item) => item.toLowerCase() === selection.toLowerCase());
  }

  setStatus(message) {
    if (this.elements.status) {
      this.elements.status.textContent = message;
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-fitment-wizard]").forEach((element) => {
    new FitmentWizard(element).init().catch((error) => {
      console.error("Fitment wizard init failed", error);
    });
  });
});
